<template>
  <div class="bg-white py-24 sm:py-32">
    <div class="mx-auto max-w-7xl px-6 lg:px-8">
      <div class="mx-auto lg:mx-0">
        <h2 class="text-3xl pb-6 font-bold tracking-tight text-gray-900 sm:text-4xl">Enrolment Process</h2>
      </div>

      <nav aria-label="Progress" class="mb-12">
    <ol role="list" class="space-y-4 md:flex md:space-x-8 md:space-y-0">
      <li v-for="step in steps" :key="step.name" class="md:flex-1">
        <a v-if="step.status === 'complete'" :href="step.href" target="_blank" class="group flex flex-col border-l-4 border-indigo-600 py-2 pl-4 hover:border-indigo-800 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4">
          <span class="text-sm font-medium text-indigo-600 group-hover:text-indigo-800">{{ step.id }}</span>
          <span class="text-sm font-medium mb-6">{{ step.name }}</span>
          <span class="font-medium text-gray-500 text-xs">{{ step.description }}</span>
        </a>
        <a v-else :href="step.href" target="_blank" class="group flex flex-col border-l-4 border-gray-200 py-2 pl-4 hover:border-gray-300 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4">
          <span class="text-sm font-medium text-gray-500 group-hover:text-gray-700">{{ step.id }}</span>
          <span class="text-sm font-medium mb-6">{{ step.name }}</span>
          <span class="font-medium text-gray-500 text-xs">{{ step.description }}</span>
        </a>
      </li>
    </ol>
  </nav>

  <div class="mx-auto lg:mx-0">
        <a href="https://forms.cloud.microsoft/r/riyWag9fzF" target="_blank">
            <button type="button" class="rounded-md bg-custom-blue mb-2 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:custom-blue-pale focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue">
                Enrolment Enquiry Form
            </button>
          </a>
          
          <div>
            <a href="/PVIS-Enrolment-Terms-and-Conditions-2024.pdf" download>
            <button type="button" class="rounded-md bg-custom-blue px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:custom-blue-pale focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue">
                Download our Enrolment Terms and Conditions [PDF]
            </button>
          </a>
          </div>
        <p class="mt-2 pb-6 leading-7 text-xs text-gray-600">For inquiries or assistance, please contact us at admin@pvis.edu.vu. To enquire, please fill out the online form.</p>
          
      </div>


      <div class="mx-auto max-w-4xl text-center">
        <h2 class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Enrolment Enquiries and Availability</h2>
        <div class="mt-6 space-y-8 text-base leading-7 text-gray-600">
          <div>
            <p>Thank you for considering Port Vila International School.</p>
            <p class="mt-4">PVIS operates on a Southern Hemisphere academic year, from January to December. Families are welcome to submit an enrolment enquiry at any time.</p>
          </div>

          <div>
            <h3 class="text-base font-semibold leading-7 text-indigo-600">Enrolment Availability</h3>
            <p class="mt-4">PVIS carefully manages enrolment numbers across each year level to support appropriate class sizes, student wellbeing and the quality of teaching and learning.</p>
            <p class="mt-4">Submitting an enquiry or application does not guarantee a place. Offers are subject to availability at the time an enrolment is confirmed and are made in accordance with the school's enrolment process.</p>
            <p class="mt-4">Where a year level is full, families may be placed on a waitlist and contacted if a suitable place becomes available.</p>
          </div>

          <div>
            <h3 class="text-base font-semibold leading-7 text-indigo-600">Priority Enrolments</h3>
            <p class="mt-4">Where demand exceeds the number of places available, priority may be given in accordance with the school's enrolment process, including:</p>
            <ul class="mx-auto mt-4 inline-block max-w-2xl list-disc space-y-2 pl-6 text-left">
              <li>students from PVIS Partner Organisations</li>
              <li>siblings of current students</li>
              <li>returning PVIS students</li>
              <li>other applicants based on availability and enrolment considerations.</li>
            </ul>
            <p class="mt-4">Families are encouraged to contact the school if they have questions about availability or the enrolment process.</p>
          </div>

          <div>
            <h3 class="text-base font-semibold leading-7 text-indigo-600">2026 and 2027 Enrolment Update</h3>
            <p class="mt-4">Planning for <strong>2027 enrolments is now underway</strong>. Availability varies across year levels, with some classes already limited or operating a waitlist.</p>
            <p class="mt-4">Families are encouraged to submit an enquiry as early as possible. For both <strong>late 2026 and 2027 entry</strong>, some places may not be confirmed until <strong>towards the end of the year or, in some cases, early 2027</strong>.</p>
            <p class="mt-4">Families are still encouraged to enquire for year levels currently operating a waitlist, as availability can change.</p>
          </div>
        </div>
      </div>
      

    </div>
  </div>
</template>

  <script setup>
const steps = [
  { id: 'Step 1', name: 'Schedule a Personal Tour', description: 'Experience our campus and understand the learning environment we offer (not compulsory, but recommended).', href: '#', status: 'complete' },
  { id: 'Step 2', name: 'Submit an Enrolment Enquiry', description: "Begin your child's admission journey by filling out the enrolment enquiry form.", href: 'https://forms.cloud.microsoft/r/riyWag9fzF', status: 'upcoming' },
  { id: 'Step 3', name: 'Assessments', href: '#', description: 'Prospective students may need to participate in assessments as part of their application.', status: 'upcoming' },
  { id: 'Step 4', name: 'Attend an Interview with the Head of School or Principal', description: "Discuss your child's educational needs and our school's offerings.", href: '#', status: 'upcoming' },
  { id: 'Step 5', name: 'Experience Morning for Early Education Students', description: "A special session for young learners to get a feel for our school.", href: '#', status: 'upcoming' },
  { id: 'Step 6', name: 'Receive an Offer of Place', href: '#', description: 'Successful applicants will be notified with an offer.', status: 'upcoming' },
]
  </script>
